import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckSquare, MessageSquare, Users, Calendar, ChevronRight, Clock, AlertCircle } from 'lucide-react';
import { dashboardApi } from '../../services/api';
import Avatar from '../shared/Avatar';
import { formatDateRelative, formatDateShort, isDeadlinePast, SUBJECT_LABELS, MEETING_TYPE_LABELS } from '../../utils/formatters';

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
    refetchInterval: 60_000,
  });

  const d = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="card animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"/>
            <div className="h-16 bg-gray-100 rounded"/>
          </div>
        ))}
      </div>
    );
  }

  const greeting = getGreeting();
  const role = d?.user?.role_label;
  const isSecretario = role === 'Secretário da Estaca';
  const isSecretarioExecutivo = role === 'Secretário Executivo';

  return (
    <div className="space-y-4">
      {/* Greeting */}
      <div className="flex items-center gap-3 py-2">
        {d?.user && <Avatar name={d.user.name} photoUrl={d.user.photo_url} size="lg" />}
        <div>
          <p className="text-sm text-gray-500">{greeting}</p>
          <h2 className="text-lg font-semibold text-gray-900">{d?.user?.name?.split(' ')[0]}</h2>
          <p className="text-xs text-gray-400">{d?.user?.role_label}</p>
        </div>
      </div>

      {/* 1. Pending Tasks */}
      <Link to="/tarefas" className="card block hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <CheckSquare size={16} className="text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Tarefas Pendentes</h3>
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <span className="text-2xl font-bold text-blue-600">{d?.pending_tasks.total || 0}</span>
            <ChevronRight size={16} />
          </div>
        </div>
        {d?.pending_tasks.urgent && d.pending_tasks.urgent.length > 0 ? (
          <div className="space-y-2">
            {d.pending_tasks.urgent.map((task: any) => (
              <div key={task.id} className="flex items-start gap-2 py-1">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${task.deadline && isDeadlinePast(task.deadline) ? 'bg-red-500' : 'bg-blue-400'}`}/>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 truncate">{task.title}</p>
                  {task.deadline && (
                    <p className={`text-xs ${isDeadlinePast(task.deadline) ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                      {isDeadlinePast(task.deadline) ? '⚠ Vencida — ' : ''}{formatDateRelative(task.deadline)}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {d.pending_tasks.total > 3 && (
              <p className="text-xs text-gray-400 pt-1">+{d.pending_tasks.total - 3} mais tarefas</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Nenhuma tarefa pendente 🎉</p>
        )}
      </Link>

      {/* 2. Entrevistas da Semana — hidden for Secretário da Estaca */}
      {!isSecretario && (
        <Link to="/entrevistas/agendadas" className="card block hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                <MessageSquare size={16} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Entrevistas da Semana</h3>
                {isSecretarioExecutivo && (
                  <p className="text-xs text-gray-400">Presidência completa</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 text-gray-400">
              <span className="text-2xl font-bold text-green-600">{d?.interviews_this_week?.length || 0}</span>
              <ChevronRight size={16} />
            </div>
          </div>
          {d?.interviews_this_week && d.interviews_this_week.length > 0 ? (
            <div className="space-y-2">
              {d.interviews_this_week.slice(0, 3).map((interview: any) => (
                <div key={interview.id} className="flex items-center gap-2 py-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{interview.person_name}</p>
                    <p className="text-xs text-gray-400">
                      {SUBJECT_LABELS[interview.subject]} — {formatDateRelative(interview.scheduled_date)}
                      {interview.scheduled_start_time && ` às ${interview.scheduled_start_time}`}
                      {isSecretarioExecutivo && interview.assigned_to_user && (
                        <span className="text-gray-300"> · {interview.assigned_to_user.name?.split(' ')[0]}</span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
              {d.interviews_this_week.length > 3 && (
                <p className="text-xs text-gray-400 pt-1">+{d.interviews_this_week.length - 3} mais entrevistas</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sem entrevistas esta semana</p>
          )}
        </Link>
      )}

      {/* 3. Próxima Reunião */}
      <Link
        to={d?.next_meeting ? `/reunioes/${d.next_meeting.id}` : '/reunioes'}
        className="card block hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
              <Calendar size={16} className="text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Próxima Reunião</h3>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </div>
        {d?.next_meeting ? (
          <div>
            <p className="text-sm font-medium text-gray-800">
              {MEETING_TYPE_LABELS[d.next_meeting.type]}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">{formatDateRelative(d.next_meeting.date)}</span>
              {d.next_meeting.time && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock size={12}/> {d.next_meeting.time}
                </span>
              )}
              <span className="text-xs text-gray-400">{(d.next_meeting as any).agenda_count || 0} itens na agenda</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Nenhuma reunião agendada</p>
        )}
      </Link>

      {/* 4. Chamados em Andamento */}
      <Link to="/chamados" className="card block hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <Users size={16} className="text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-800">Chamados em Andamento</h3>
          </div>
          <div className="flex items-center gap-1 text-gray-400">
            <span className="text-2xl font-bold text-purple-600">{d?.callings_in_progress || 0}</span>
            <ChevronRight size={16} />
          </div>
        </div>
      </Link>
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia,';
  if (hour < 18) return 'Boa tarde,';
  return 'Boa noite,';
}
